import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Chip,
  SelectChangeEvent,
  FormControlLabel,
  Checkbox,
  ListItemText,
  FormHelperText
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { 
  collection, 
  query, 
  getDocs, 
  doc, 
  getDoc, 
  deleteDoc, 
  updateDoc, 
  serverTimestamp,
  runTransaction
} from 'firebase/firestore';
import { createUserWithEmailAndPassword, getAuth, deleteUser } from 'firebase/auth';
import { db } from '../../../shared/utils/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { buildFeatureFlagUpdate } from './featureFlags';
import { managedLocations } from '../../auth/managedLocations';

interface User {
  id: string;
  uid: string;
  email: string;
  displayName: string;
  role: string;
  coach_id?: string;
  assigned_location?: string;
  assigned_locations?: string[];
  created_at: any;
  updated_at: any;
  photoURL?: string;
  feature_flags?: Record<string, boolean>;
}

interface FormData {
  id?: string;
  uid?: string;
  email?: string;
  displayName?: string;
  role: string;
  coach_id?: string | null;
  /** A manager's locations; the first is kept as their primary `assigned_location`. */
  assigned_locations: string[];
  photoURL?: string;
  lactate_testing?: boolean;
  mindbody_schedule?: boolean;
  new_dashboard?: boolean;
}

interface UserManagementProps {
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setSnackbar: (snackbar: { open: boolean; message: string; type: 'success' | 'error' }) => void;
}

/**
 * The role-dependent fields sent to the createUser / updateUser callables.
 * Managers keep their staff id (it links their own performance page) and send
 * every location; the first is also sent as `assigned_location` for callables
 * deployed before managers could oversee several sites.
 */
const staffAndLocationFields = (form: FormData) => ({
  coach_id: form.role === 'USER' || form.role === 'MANAGER' ? form.coach_id || null : null,
  assigned_location: form.role === 'MANAGER' ? form.assigned_locations[0] ?? null : null,
  assigned_locations: form.role === 'MANAGER' ? form.assigned_locations : null,
});

const UserManagement: React.FC<UserManagementProps> = ({ setLoading, setError, setSnackbar }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [locations, setLocations] = useState<string[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<FormData>({
    email: '',
    displayName: '',
    role: 'USER',
    coach_id: null,
    assigned_locations: [],
    photoURL: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [password, setPassword] = useState('');

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const q = query(collection(db, 'users'));
      const querySnapshot = await getDocs(q);
      
      const fetchedUsers: User[] = [];
      querySnapshot.forEach((doc) => {
        fetchedUsers.push({
          id: doc.id,
          ...doc.data()
        } as User);
      });
      
      setUsers(fetchedUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [setLoading, setError]);

  const fetchLocations = useCallback(async () => {
    try {
      // Use actual location names instead of dummy locations
      const actualLocations = [
        'UFIT Singapore CBD, 21 Club St Spore 069410',
        'UFIT Orchard',
        'UFIT City Hall',
        'UFIT One North, 01 Galaxis Building, Spore 138632'
      ];
      setLocations(actualLocations);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchLocations();
  }, [fetchUsers, fetchLocations]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear validation error when field is updated
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };

  const handleLocationsChange = (e: SelectChangeEvent<string[]>) => {
    const { value } = e.target;
    // Location names contain commas, so a string value (browser autofill) is one location.
    setFormData({ ...formData, assigned_locations: Array.isArray(value) ? value : [value] });
    if (formErrors.assigned_locations) {
      setFormErrors({ ...formErrors, assigned_locations: '' });
    }
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear validation error when field is updated
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    
    // Clear validation error when field is updated
    if (formErrors.password) {
      setFormErrors({
        ...formErrors,
        password: ''
      });
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (!formData.email) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Invalid email address';
    }
    
    if (!formData.displayName) {
      errors.displayName = 'Display name is required';
    }
    
    if (!formData.role) {
      errors.role = 'Role is required';
    }
    
    if (formData.role === 'MANAGER' && formData.assigned_locations.length === 0) {
      errors.assigned_locations = 'Pick at least one location for a Manager';
    }
    
    if (formData.role === 'USER' && !formData.coach_id) {
      errors.coach_id = 'Coach ID is required for Coaches';
    }
    
    if (!selectedUser && !password) {
      errors.password = 'Password is required for new users';
    } else if (!selectedUser && password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddUser = () => {
    setFormData({
      email: '',
      displayName: '',
      role: 'USER',
      coach_id: null,
      assigned_locations: [],
      photoURL: '',
      lactate_testing: false,
      mindbody_schedule: true,
      new_dashboard: false
    });
    setPassword('');
    setFormErrors({});
    setSelectedUser(null);
    setOpenDialog(true);
  };

  const handleEditUser = (user: User) => {
    setFormData({
      email: user.email,
      displayName: user.displayName,
      role: user.role,
      coach_id: user.coach_id || null,
      assigned_locations: managedLocations(user),
      photoURL: user.photoURL || '',
      lactate_testing: user.feature_flags?.lactate_testing === true,
      // On unless explicitly opted out (see Dashboard).
      mindbody_schedule: user.feature_flags?.mindbody_schedule !== false,
      new_dashboard: user.feature_flags?.new_dashboard === true
    });
    setSelectedUser(user);
    setPassword('');
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleDeleteUser = (user: User) => {
    setSelectedUser(user);
    setOpenDeleteDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setSelectedUser(null);
  };

  const handleDeleteDialogClose = () => {
    setOpenDeleteDialog(false);
    setSelectedUser(null);
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
      const functions = getFunctions();
      
      if (selectedUser) {
        // Update existing user using Cloud Function
        const updateUserFn = httpsCallable(functions, 'updateUser');
        const updateData = {
          uid: selectedUser.id,
          displayName: formData.displayName,
          role: formData.role,
          ...staffAndLocationFields(formData),
          photoURL: formData.photoURL
        };
        
        console.log('Updating user with data:', updateData);
        const result = await updateUserFn(updateData);
        console.log('Update user result:', result);

        // Feature flags are not handled by the updateUser callable; admins may
        // patch the user doc directly (allowed by firestore.rules).
        await updateDoc(doc(db, 'users', selectedUser.id), {
          ...buildFeatureFlagUpdate({
            lactate_testing: formData.lactate_testing === true,
            mindbody_schedule: formData.mindbody_schedule === true,
            new_dashboard: formData.new_dashboard === true
          }),
          updated_at: serverTimestamp()
        });

        setSnackbar({
          open: true,
          message: 'User updated successfully',
          type: 'success'
        });
      } else {
        // Create new user using Cloud Function
        try {
          console.log('Creating user with Cloud Function...');
          const createUserFn = httpsCallable(functions, 'createUser');
          const userData = {
            email: formData.email,
            password: password,
            displayName: formData.displayName,
            role: formData.role,
            ...staffAndLocationFields(formData),
            photoURL: formData.photoURL
          };
          
          console.log('User data being sent:', { ...userData, password: '********' });
          const result = await createUserFn(userData);
          console.log('Create user result:', result);

          // The createUser callable does not accept feature flags, so set them
          // with a follow-up update (mirrors the edit path). The callable
          // returns { success, uid }.
          const newUid = (result.data as any)?.uid;
          if (newUid) {
            await updateDoc(doc(db, 'users', newUid), {
              ...buildFeatureFlagUpdate({
                lactate_testing: formData.lactate_testing === true,
                mindbody_schedule: formData.mindbody_schedule === true,
                new_dashboard: formData.new_dashboard === true
              }),
              updated_at: serverTimestamp()
            });
          }

          setSnackbar({
            open: true,
            message: 'User created successfully',
            type: 'success'
          });
        } catch (error) {
          console.error('Error creating user with Cloud Function:', error);
          setSnackbar({
            open: true,
            message: `Error creating user: ${error instanceof Error ? error.message : 'Unknown error'}`,
            type: 'error'
          });
          throw error;
        }
      }
      
      handleDialogClose();
      fetchUsers();
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      setSnackbar({
        open: true,
        message: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`,
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedUser) return;
    
    try {
      setLoading(true);
      const functions = getFunctions();
      
      // Delete user using Cloud Function
      console.log('Deleting user with Cloud Function...');
      const deleteUserFn = httpsCallable(functions, 'deleteUser');
      const result = await deleteUserFn({ uid: selectedUser.id });
      console.log('Delete user result:', result);
      
      setSnackbar({
        open: true,
        message: 'User deleted successfully',
        type: 'success'
      });
      
      handleDeleteDialogClose();
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      setError('Failed to delete user. Please try again.');
      setSnackbar({
        open: true,
        message: 'Error: ' + (error instanceof Error ? error.message : 'Unknown error'),
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2">
          User Management
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddUser}
        >
          Add User
        </Button>
      </Box>
      
      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.main' }}>
              <TableCell sx={{ color: 'white' }}>Name</TableCell>
              <TableCell sx={{ color: 'white' }}>Email</TableCell>
              <TableCell sx={{ color: 'white' }}>Role</TableCell>
              <TableCell sx={{ color: 'white' }}>Details</TableCell>
              <TableCell sx={{ color: 'white' }}>Created</TableCell>
              <TableCell sx={{ color: 'white' }} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.displayName}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <Chip 
                    label={user.role} 
                    color={
                      user.role === 'ADMIN' 
                        ? 'error' 
                        : user.role === 'MANAGER' 
                        ? 'warning' 
                        : 'primary'
                    } 
                    size="small" 
                  />
                </TableCell>
                <TableCell>
                  {user.role === 'MANAGER' && managedLocations(user).length > 0 && (
                    `${managedLocations(user).length > 1 ? 'Locations' : 'Location'}: ${managedLocations(user).join(' + ')}`
                  )}
                  {user.role === 'USER' && user.coach_id && (
                    `Coach ID: ${user.coach_id}`
                  )}
                </TableCell>
                <TableCell>
                  {user.created_at ? new Date(user.created_at.seconds * 1000).toLocaleDateString() : 'N/A'}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit">
                    <IconButton onClick={() => handleEditUser(user)}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton onClick={() => handleDeleteUser(user)}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Add/Edit User Dialog */}
      <Dialog open={openDialog} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedUser ? 'Edit User' : 'Add New User'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              name="email"
              label="Email"
              fullWidth
              margin="normal"
              value={formData.email || ''}
              onChange={handleInputChange}
              error={!!formErrors.email}
              helperText={formErrors.email}
              disabled={!!selectedUser}
            />
            
            <TextField
              name="displayName"
              label="Display Name"
              fullWidth
              margin="normal"
              value={formData.displayName || ''}
              onChange={handleInputChange}
              error={!!formErrors.displayName}
              helperText={formErrors.displayName}
            />
            
            {!selectedUser && (
              <TextField
                name="password"
                label="Password"
                type="password"
                fullWidth
                margin="normal"
                value={password}
                onChange={handlePasswordChange}
                error={!!formErrors.password}
                helperText={formErrors.password}
              />
            )}
            
            <FormControl fullWidth margin="normal" error={!!formErrors.role}>
              <InputLabel id="role-label">Role</InputLabel>
              <Select
                labelId="role-label"
                name="role"
                value={formData.role || ''}
                label="Role"
                onChange={handleSelectChange}
              >
                <MenuItem value="USER">Coach (USER)</MenuItem>
                <MenuItem value="MANAGER">Manager</MenuItem>
                <MenuItem value="ADMIN">Administrator</MenuItem>
              </Select>
              {formErrors.role && (
                <Typography variant="caption" color="error">
                  {formErrors.role}
                </Typography>
              )}
            </FormControl>
            
            {formData.role === 'MANAGER' && (
              <FormControl fullWidth margin="normal" error={!!formErrors.assigned_locations}>
                <InputLabel id="location-label">Assigned Locations</InputLabel>
                <Select
                  labelId="location-label"
                  multiple
                  value={formData.assigned_locations}
                  label="Assigned Locations"
                  onChange={handleLocationsChange}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((location) => (
                        <Chip key={location} label={location} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {[...locations, ...formData.assigned_locations.filter((location) => !locations.includes(location))].map((location) => (
                    <MenuItem key={location} value={location}>
                      <Checkbox checked={formData.assigned_locations.includes(location)} size="small" />
                      <ListItemText primary={location} />
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {formErrors.assigned_locations ||
                    'They see clients, staff, alerts and leave for every location picked. The first picked is their primary.'}
                </FormHelperText>
              </FormControl>
            )}
            
            {(formData.role === 'USER' || formData.role === 'MANAGER') && (
              <TextField
                name="coach_id"
                label={formData.role === 'USER' ? 'Coach ID' : 'Coach ID (optional)'}
                fullWidth
                margin="normal"
                value={formData.coach_id || ''}
                onChange={handleInputChange}
                error={!!formErrors.coach_id}
                helperText={
                  formErrors.coach_id ||
                  (formData.role === 'USER'
                    ? 'Unique identifier for this coach'
                    : 'Their staff id, which links their own performance page and schedule')
                }
              />
            )}
            
            <TextField
              name="photoURL"
              label="Photo URL"
              fullWidth
              margin="normal"
              value={formData.photoURL || ''}
              onChange={handleInputChange}
              helperText="URL to user's profile photo (optional)"
            />

            <FormControlLabel
              control={
                <Checkbox
                  name="lactate_testing"
                  checked={formData.lactate_testing === true}
                  onChange={(e) =>
                    setFormData({ ...formData, lactate_testing: e.target.checked })
                  }
                />
              }
              label="Lactate Threshold Testing access"
              sx={{ mt: 1 }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  name="mindbody_schedule"
                  checked={formData.mindbody_schedule === true}
                  onChange={(e) =>
                    setFormData({ ...formData, mindbody_schedule: e.target.checked })
                  }
                />
              }
              label="Today schedule from Mindbody (untick to use the client list instead)"
              sx={{ display: 'flex' }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  name="new_dashboard"
                  checked={formData.new_dashboard === true}
                  onChange={(e) =>
                    setFormData({ ...formData, new_dashboard: e.target.checked })
                  }
                />
              }
              label="New dashboard (Today and Actions, in development)"
              sx={{ display: 'flex' }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary"
          >
            {selectedUser ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleDeleteDialogClose}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {selectedUser?.displayName}? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
